// ── Generic CRUD table ─────────────────────────────────────────
import {API_URL} from "../../../constants/constants";
import {useEffect, useRef, useState} from "react";
import {apiClient, authHeaders} from "../../../api/apiClient";
import {token} from "../../../api/api";
import {useTranslation} from "react-i18next";
import {Modal} from "../../User_Side/modal/Modal";
import {GalleryPhotosManager} from "../../User_Side/gallery-photos-manager/GalleryPhotosManager";

export const CrudTable = ({cols, rows, onDelete, onEdit}: {
    cols: string[];
    rows: any[];
    onDelete?: (id: number) => void;
    onEdit?: (row: any) => void;
}) => {
    const [sortCol, setSortCol] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [filters, setFilters] = useState<Record<string, string>>({});

    const handleSort = (col: string) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
    };
    const getCellValue = (row: any, col: string): string => {
        if (col === 'category' || col === 'collection' || col === 'items') return row[col]?.name ?? '';
        if (col === 'active') return row[col] ? 'oui' : 'non';
        return String(row[col] ?? '');
    };

    const filtered = rows.filter(row =>
        Object.entries(filters).every(([col, val]) => {
            if (!val) return true;
            if (col === 'active' || col === 'sold') {
                const cellVal = getCellValue(row, col);
                if (['oui', 'yes', 'true'].includes(val.toLowerCase())) return cellVal === "true";
                if (['non', 'no', 'false'].includes(val.toLowerCase())) return cellVal === "false";
            }
            return getCellValue(row, col).toLowerCase().includes(val.toLowerCase());
        })
    );

    const sorted = [...filtered].sort((a, b) => {
        if (!sortCol) return 0;
        const va = getCellValue(a, sortCol);
        const vb = getCellValue(b, sortCol);

        const na = Number(va);
        const nb = Number(vb);

        if (!isNaN(na) && !isNaN(nb)) {
            return sortDir === 'asc' ? na - nb : nb - na;
        }
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    const renderCell = (row: any, c: string) => {
        if (c === 'imageUrl' && row[c]) return <img src={`${API_URL}${row[c]}`} alt=""/>;
        if (c === 'category' || c === 'collection' || c === 'items') return row[c]?.name || '—';
        if (c === 'active' || c === 'sold') return row[c] ? 'Oui' : 'Non';
        if (c === 'role') return (
            <span style={{
                background: row[c] === 'ADMIN' ? 'rgba(254,21,24,0.1)' : 'rgba(92,215,222,0.1)',
                color: row[c] === 'ADMIN' ? '#fe1518' : '#3ab8bf'
            }}>{row[c]}</span>
        );
        if (c === 'status') return (
            <span style={{ background: 'rgba(92,215,222,0.1)', color: '#3ab8bf' }}>{row[c]}</span>
        );
        return String(row[c] ?? '—');
    };

    return (
        <div className="table-container">
            <table className="admin-table">
                <thead>
                <tr>
                    {cols.map(c => (
                        <th key={c} onClick={() => handleSort(c)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            {c}
                            {sortCol === c ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                        </th>
                    ))}
                    <th></th>
                </tr>
                <tr>
                    {cols.map(c => (
                        <th key={c} style={{ fontWeight: 'normal', padding: '4px' }}>
                            {c !== 'imageUrl' && (
                                <input
                                    type="text"
                                    placeholder={`Filtrer…`}
                                    value={filters[c] ?? ''}
                                    onChange={e => setFilters(f => ({ ...f, [c]: e.target.value }))}
                                    onClick={e => e.stopPropagation()}
                                    style={{ width: '100%', fontSize: '0.75rem', padding: '2px 6px' }}
                                />
                            )}
                        </th>
                    ))}
                    <th>
                        <button
                            className="btn-admin btn-admin-secondary"
                            onClick={() => { setFilters({}); setSortCol(null); }}
                            style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                        >
                            Reset
                        </button>
                    </th>
                </tr>
                </thead>
                <tbody>
                {sorted.map(row => (
                    <tr key={row.id}>
                        {cols.map(c => <td key={c}>{renderCell(row, c)}</td>)}
                        <td>
                            {onEdit && <button className="btn-admin btn-admin-secondary" onClick={() => onEdit(row)}>Éditer</button>}
                            {onDelete && <button className="btn-admin btn-admin-danger" onClick={() => onDelete(row.id)}>Supprimer</button>}
                        </td>
                    </tr>
                ))}
                {sorted.length === 0 && (
                    <tr><td colSpan={cols.length + 1}>Aucune donnée</td></tr>
                )}
                </tbody>
            </table>
        </div>
    );
};

export const SimpleCrudSection = ({endpoint, label}: { endpoint: string; label: string }) => {
    const [items, setItems] = useState<any[]>([]);
    const [name, setName] = useState('');
    const [active, setActive] = useState(true);
    const [editing, setEditing] = useState<any>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [image, setImage] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [modal, setModal] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const {t} = useTranslation()

    const load = () =>
        apiClient.get(endpoint, authHeaders(token()))
            .then((r: any) => setItems(r.data))
            .catch(console.error);

    useEffect(() => {
        load();
    }, []);


    const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setImage(f);
        if (f) setPreview(URL.createObjectURL(f));
    };

    const handleSubmit = async () => {
        if (!name.trim()) return;
        const fd = new FormData();
        fd.append('name', name);
        fd.append('active', active ? 'true' : 'false');
        if (image) fd.append('image', image);
        try {
            if (editing) {
                await apiClient.put(`${endpoint}/${editing.id}`, fd, authHeaders(token()));
            } else {
                const activeItems = items.filter(item => item.active === true);
                if (endpoint === "/home-sliding-photos" && activeItems.length >= 9) {
                    setModal(true);
                    return;
                }
                await apiClient.post(endpoint, fd, authHeaders(token()));
                setSubmitted(true)
            }
            setName('');
            setEditing(null);
            setImage(null);
            setPreview(null);
            setActive(true);
            load();
        } catch (e) {
            console.error(e);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(`${t("admin.simple-crud.delete")} ?`)) return;
        await apiClient.delete(`${endpoint}/${id}`, authHeaders(token()));
        load();
    };

    return (
        <div>
            <div className="admin-card">
                {modal && (
                    <Modal
                        message={t("admin.simple-crud.9-active-photos-max")}
                        onCancel={() => setModal(false)}
                        onConfirm={() => setModal(false)}
                        submitted={submitted}
                        confirmationText={"Validé"}
                    />
                )}
                <p className="admin-section-title">{editing ? `${t("admin.simple-crud.edit")}` : `${t("admin.simple-crud.add")}`} {label.toLowerCase()}</p>
                <div>
                    <div className="admin-form">
                        <div className="admin-field">
                            <label>{t("admin.simple-crud.name")}</label>
                            <input type="text" value={name} onChange={e => setName(e.target.value)}
                                   onKeyDown={e => e.key === 'Enter' && handleSubmit()}/>
                        </div>
                        <div className="admin-field">
                            <label>{t("admin.simple-crud.active")}</label>
                            <input type="checkbox" checked={active} onChange={e => setActive(!active)}
                                   onKeyDown={e => e.key === 'Enter' && handleSubmit()}/>
                        </div>

                        <div>
                            <div className="admin-upload-zone" onClick={() => fileRef.current?.click()}>
                                <input ref={fileRef} id="file-input" type="file" accept="image/*"
                                       onChange={handleImage}/>
                                {preview ? <div className="admin-preview"><img src={preview} alt="preview"/></div> :
                                    <p>{t("admin.products.click-to-select-image")}</p>}
                            </div>
                        </div>
                        <div>
                            <button className="btn-admin btn-admin-primary"
                                    onClick={handleSubmit }>{editing ? `${t("admin.simple-crud.update")}` : `${t("admin.simple-crud.add")}`}</button>
                            {editing && <button className="btn-admin btn-admin-secondary" onClick={() => {
                                setEditing(null);
                                setName('');
                            }}>{t("admin.simple-crud.cancel")}</button>}
                        </div>
                        {editing && endpoint === '/home-sliding-photos' && (
                            <GalleryPhotosManager homeSlidingPhotoId={editing.id} />
                        )}
                    </div>
                </div>
            </div>
            <div className="admin-card">
                <CrudTable cols={['id', 'name', 'imageUrl', 'active', 'createdAt']} rows={items} onDelete={handleDelete}
                           onEdit={row => {
                               setEditing(row);
                               setName(row.name);
                           }}/>
            </div>
        </div>
    );
};
